<script>
  import { gameNamesById } from '@/data/games';
  import { BANK_IDS } from '@/store';
  import { GameLauncher } from '@/mixins/game-launcher';

  export default {
    name: 'HostedRooms',

    mixins: [GameLauncher],

    data: () => ({
      gameNamesById,
      headers: [
        {
          text: 'Game',
          align: 'start',
          sortable: false,
          value: 'name',
          width: '8rem',
        },
        { text: 'Buy in', value: 'buy-in', align: 'right', width: '6rem', sortable: false },
        { text: 'Room ID', value: 'room-id', align: 'center', class: 'room-id-column', sortable: false },
        { text: 'Actions', value: 'actions', align: 'right' },
      ],
    }),

    computed: {
      items() {
        return [...(this.$store.getters.getPrivateRoomsList || [])].sort((a, b) => {
          // First, sort by active status
          if (a.active !== b.active) {
            return b.active - a.active;
          }

          return new Date(b.dateCreated) - new Date(a.dateCreated);
        });
      },
      currentUserId() {
        return this.$store.getters.getCurrentUserID;
      },
    },

    created() {
      Promise.all([
        this.$store.dispatch('getPrivateRoomsByUser', { gameId: 14 }),
        this.$store.dispatch('getPrivateRoomsByUser', { gameId: 15 }),
        this.$store.dispatch('getCurrentUserID'),
      ]);
    },

    methods: {
      isUnityGame(gameId) {
        if (this.$store.getters.getActiveGames.length > 0) {
          const game = this.$store.getters.getActiveGames.find((game) => game.key === String(gameId));
          return game ? game.unityGame : false;
        } else {
          return false;
        }
      },
      async enterRoom({ gameId, privateRoomId, buyIn, hostId, bankId }) {
        const addFriendUrl = process.env.VUE_APP_ADD_FRIEND_URL;

        const { balance } = this.$store.getters.getUserTotalBalance;
        const hasBalance = bankId === BANK_IDS.MQC ? balance.MQC > 0 : balance.MMC > 0;
        let isBalanceSufficient = bankId === BANK_IDS.MQC ? balance.MQC >= (buyIn || 0) : balance.MMC >= (buyIn || 0);

        if (!hasBalance || !isBalanceSufficient) {
          this.$store.dispatch('showConfirmDialog', {
            title: 'Insufficient balance',
            text: 'In order to accept this invitation, please deposit to your balance.',
            yesText: 'Deposit Now',
            noText: 'Cancel',
            callback: () => {
              this.$store.dispatch('setWalletDialog', {
                value: true,
                tab: 'purchase',
              });
            },
          });
          return;
        }
        if (!this.isUnityGame(gameId)) {
          await this.enterPrivateRoom(privateRoomId, 'CAF', addFriendUrl);
        } else {
          const homeUrl = process.env.VUE_APP_CHALLENGE_URL;
          await this.launchGameUnity(
            gameId,
            homeUrl,
            addFriendUrl,
            'extended',
            buyIn,
            false,
            hostId,
            privateRoomId,
            bankId,
          );
        }
      },
      async closeRoom({ privateRoomId, bankId, gameId }) {
        await this.$store.dispatch('requestGameplayTokenAs');
        const token = this.$store.getters.getGameplayToken;

        const data = {
          privateRoomId: privateRoomId,
          privateRoomToken: token,
          bankId: bankId,
          gameId,
          isUnity: this.isUnityGame(gameId),
        };

        await this.$store.dispatch('deactivatePrivateRoom', data);

        if (data.isUnity) {
          await this.$store.dispatch('getPrivateRoomsByUser', { gameId: gameId });
        }
      },

      canCloseRoom(item) {
        return !this.amIKicked(item) && item.active && this.currentUserId === item.hostId;
      },
      amIKicked(room) {
        const me = Object.values(room.playersInvited || {}).find(
          (i) => i?.displayName === this.userDetails?.displayName,
        );

        return me && me.status === 'KICKED';
      },

      getFormattedBuyInValue(item) {
        switch (item?.bankId) {
          case BANK_IDS.MQC:
            return `${(item?.buyIn / 100).toLocaleString()} QC`;
          case BANK_IDS.MMC:
            return `$${(item?.buyIn / 100).toLocaleString()}`;
          default:
            return '–';
        }
      },
    },
  };
</script>

<template>
  <div>
    <div class="font-size-24 white--text font-weight-600 mb-4">My Hosted Rooms</div>

    <v-data-table
      class="hosted-rooms-table"
      :class="{ 'no-headers': !items.length }"
      :headers="headers"
      :items="items"
      hide-default-footer
      disable-sort
      no-data-text="Play your first game"
      mobile-breakpoint="560"
    >
      <template #item.name="{ item }">
        <span class="font-weight-800">{{ gameNamesById[item.gameId] }}</span>
      </template>
      <template #item.buy-in="{ item }">
        <span class="text-gold font-weight-600">{{ getFormattedBuyInValue(item) }}</span>
      </template>
      <template #item.room-id="{ item }">
        <span>#{{ item.customRoomId }}</span>
      </template>
      <template #item.actions="{ item }">
        <div class="actions-row">
          <action-button>
            <template #action="{ loading, clickHandler }">
              <v-btn
                class="action-btn"
                :loading="loading"
                :disabled="!item.active"
                @click="clickHandler(enterRoom, item)"
              >
                Enter Room
              </v-btn>
            </template>
          </action-button>
          <action-button>
            <template #action="{ loading, clickHandler }">
              <v-btn
                icon
                class="action-btn error-red--text close-btn ml-2"
                :loading="loading"
                :disabled="!item.active || !canCloseRoom(item)"
                @click="clickHandler(closeRoom, item)"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
          </action-button>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<style scoped lang="scss">
  @import 'src/scss/mixins';

  @include dark-table();

  .text-gold {
    color: var(--v-gold-base);
    font-weight: 500;
  }

  .actions-row {
    display: flex;
    align-items: center;
    justify-content: end;
    gap: 4px;
  }
</style>
